
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, Music } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatLaiton = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Laiton - Rachat Robinetterie, Instruments Musique | Groupe Fabre"
        description="Rachat de laiton au meilleur prix : robinetterie, instruments de musique, décorations. Pesée certifiée et paiement immédiat."
        canonical="/nos-services/achat-metaux/laiton"
        keywords="achat laiton, rachat robinetterie laiton, instruments musique, prix laiton, bronze"
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <Music className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat de Laiton</h1>
                  <p className="text-xl mt-2 opacity-90">Rachat de robinetterie et instruments de musique</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat de laiton - Alliage cuivre-zinc
                </h2>
                <p className="text-gray-600 mb-6">
                  Le <strong>laiton</strong> est un alliage de cuivre et zinc très apprécié pour ses qualités esthétiques. 
                  <strong>Groupe Fabre Environnement</strong> rachète tous types de laiton : 
                  robinetterie, instruments de musique, décorations et pièces industrielles.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Types de laiton rachetés</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-yellow-600 text-white mr-3">•</span>
                    <span><strong>Robinetterie :</strong> robinets, valves, raccords</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-yellow-600 text-white mr-3">•</span>
                    <span><strong>Instruments :</strong> trompettes, saxophones, cuivres</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-yellow-600 text-white mr-3">•</span>
                    <span><strong>Décorations :</strong> objets d'art, luminaires</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-yellow-600 text-white mr-3">•</span>
                    <span><strong>Industriel :</strong> paliers, engrenages, pièces mécaniques</span>
                  </li>
                </ul>

                <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
                  <h4 className="font-bold text-yellow-800 mb-3">Propriétés du laiton</h4>
                  <ul className="text-yellow-700 text-sm space-y-2">
                    <li>• Résistance à la corrosion et à l'usure</li>
                    <li>• Facilité d'usinage et de formage</li>
                    <li>• Aspect esthétique doré apprécié</li>
                    <li>• Recyclable avec conservation des propriétés</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Expertise laiton</h3>
                <p className="text-gray-600 mb-6">
                  Notre expertise nous permet d'identifier les différentes nuances de laiton 
                  et de vous proposer la meilleure valorisation selon la composition.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-yellow-600 mr-3" />
                    <span>Prix selon teneur cuivre</span>
                  </div>
                  <div className="flex items-center">
                    <Music className="h-5 w-5 text-yellow-600 mr-3" />
                    <span>Expertise instruments</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-yellow-600 mr-3" />
                    <span>Test de composition</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-yellow-600 text-white rounded-md font-medium hover:bg-yellow-700 transition-colors">
                    Demander prix laiton
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Types de laiton */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Classifications laiton et compositions
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Laiton Rouge</h3>
                <p className="text-sm text-gray-600 mb-3">
                  85% cuivre, 15% zinc - robinetterie haut de gamme
                </p>
                <div className="text-green-600 font-medium">Prix le plus élevé</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Laiton Jaune</h3>
                <p className="text-sm text-gray-600 mb-3">
                  70% cuivre, 30% zinc - usage général
                </p>
                <div className="text-orange-600 font-medium">Prix standard</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Laiton Naval</h3>
                <p className="text-sm text-gray-600 mb-3">
                  60% cuivre, 40% zinc - applications marines
                </p>
                <div className="text-blue-600 font-medium">Prix spécifique</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatLaiton;
